import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Pagination from 'react-js-pagination';

import productActions from '../../../store/actions/productActions';
import categoriesActions from '../../../store/actions/categoriesActions';
import Search from '../../../components/Search/Search';
import CategoriesMobile from '../../../components/Categories/CategoriesMobile/CategoriesMobile';
import AdvertList from '../../../components/AdvertList/AdvertList';
import MainTabs from '../../../components/Categories/MainTabs';
import BreadCrumbs from '../../../components/UI/BreadCrumbs';
import { getCategoryWithSlug, getQuerySlug } from '../../../helpers';
import urlHelper from '../../../helpers/url.helper';
import IconLongArrow from '../../../components/UI/Icons/IconLongArrow';
import MetaTags from '../../../components/MetaTags/MetaTags';
import Container from '../../../components/UI/Container/Container';
import AdvertsFilter from '../../../components/AdvertsFilter/AdvertsFilter';

const Category = ({ query }) => {
  const {
    products, categories,
  } = useSelector((state) => ({
    products: state.products,
    categories: state.categories.categoriesList,
  }));
  const { language } = useSelector((state) => state.language);
  const router = useRouter();
  const dispatch = useDispatch();
  const [slugs, setSlugs] = useState([]);
  const [fullSlug, setFullSlug] = useState(null);
  const [defaultQuery] = useState(query);
  const [search, setSearch] = useState('');

  const crumbs = useMemo(() => {
    const output = [
      {
        title: (language || process.env.DEFAULT_LANG) === 'ru-RU' ? 'Главная' : 'Home',
        href: '/',
      },
    ];
    let cats = categories.data;
    slugs.forEach((item, index) => {
      if (index === slugs.length - 1) {
        output.push({
          title: getCategoryWithSlug(item, cats).title,
          href: '',
          as: '',
        });
      } else {
        const category = getCategoryWithSlug(item, cats);
        output.push({
          title: category.title,
          as: `/category/${slugs.slice(0, index + 1).join('/')}`,
          href: '/category/[...slug]',
        });
        cats = category.children;
      }
    });
    return output;
  }, [slugs, categories, language]);

  useEffect(() => {
    if (categories.data.length) {
      const newSlugs = router.asPath.substring(10, router.asPath.length)
        .split('?')[0]
        .split('/');

      setSlugs(newSlugs);
      setFullSlug(getQuerySlug(newSlugs, categories.data));
    }
  }, [router.asPath, categories.data.length]);

  useEffect(() => {
    if (fullSlug) {
      let page = 1;
      if (defaultQuery.page && defaultQuery.page > 1) {
        page = defaultQuery.page;
      }
      const params = { category: fullSlug, page };
      if (defaultQuery.query) {
        params.search = defaultQuery.query;
        params.searchFields = 'title;link;tag;username';
        setSearch(defaultQuery.query);
      }
      dispatch(productActions.getAllProducts(params));
    }
  }, [fullSlug]);

  useEffect(() => {
    if (!categories.data.length) {
      const headers = {};
      headers['accept-language'] = localStorage.getItem('lang') || process.env.DEFAULT_LANG || 'en-US';
      dispatch(categoriesActions.getCategories(headers));
    }
  }, []);

  const searchChangeHandler = (data) => {
    if (fullSlug) {
      let params = {
        category: fullSlug,
        page: 1,
      };
      if (data) {
        params = {
          ...params,
          ...data,
        };
      }
      setSearch(params.search);
      router.replace(router.route, urlHelper.buildUrl(router, { page: 1, query: params.search }));
      dispatch(productActions.getAllProducts(params));
    }
  };

  const handlePageChange = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    const params = { category: fullSlug, page };
    if (search) {
      params.search = search;
      params.searchFields = 'title;link;tag;username';
    }
    dispatch(productActions.getAllProducts(params));
    router.replace(router.route, urlHelper.buildUrl(router, { query: search, page }));
  };

  const handleRefresh = () => {
    if (!products.loading && fullSlug) {
      let page = products?.pagination?.current_page || 1;
      if (defaultQuery.page && defaultQuery.page > 1) {
        page = defaultQuery.page;
      }
      const params = { category: fullSlug, page };
      if (defaultQuery.query) {
        params.search = defaultQuery.query;
        params.searchFields = 'title;link;tag;username';
        setSearch(defaultQuery.query);
      }
      dispatch(productActions.getAllProducts(params));
    }
  };

  return (
    <>
      <MetaTags
        metaTitle={`${language === 'ru-RU' ? 'Товары категории' : 'Products in the category'} ${crumbs[crumbs.length - 1].title}`}
        metaDescription={`${language === 'ru-RU' ? 'Купить товар в категории' : 'Buy a product in the category'} ${crumbs[crumbs.length - 1].title}`}
      />
      <Container>
        <BreadCrumbs crumbs={crumbs} />
        <AdvertsFilter slugs={slugs} onRefresh={handleRefresh}/>
        <AdvertList
          products={products.allProducts}
          isLoading={products.isLoading}
        />
        {
          products.pagination.total_pages > 1 && (
            <Pagination
              activePage={products.pagination.current_page}
              itemsCountPerPage={products.pagination.per_page}
              totalItemsCount={products.pagination.total}
              pageRangeDisplayed={8}
              onChange={handlePageChange}
              hideFirstLastPages
              nextPageText={<IconLongArrow color="#4579f5" />}
              prevPageText={<IconLongArrow color="#4579f5" />}
              itemClassNext="next"
              itemClassPrev="prev"
            />
          )
        }
      </Container>
    </>
  );
};

export async function getServerSideProps(context) {
  return {
    props: {
      query: context.query,
    },
  };
}

export default Category;
